import { HubConnectionState } from "@microsoft/signalr";
import { ref, watch } from "vue";



import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { OperatorDao } from "@/data/dao/service/OperatorDao";
import { SocketEventHelper } from "@/data/dao/socket/SocketEventHelper";
import { dataEvents } from "@/data/events/dataEvents";
import { FulfillmentHandling, FulfillmentPositionDisplayMode, FulfillmentProductionState, FulfillmentState, OrderState } from "@/data/model/Enums";
import type { IOrderOutstandingChangedEvent, IPayLaterOrderConfirmedEvent } from "@/data/model/Events";
import type { IFulfillmentGroup } from "@/data/model/FulfillmentGroup";
import type { IExtendedOperatorFulfillment, IOperatorFulfillment } from "@/data/model/Operator";
import { Logging } from "@/data/util/Logging";



import { logError } from "@/shared/logging/errors";



import { Popup } from "@/components/dialog/Popup";



import { OperatorClient } from "@/operator/OperatorClient";
import { FulfillmentHelper } from "@/operator/data/FulfillmentHelper";
import { operatorEvents } from "@/operator/events/operatorEvents";
import { trans } from "@/operator/localization/Trans";


// this class is responsible for keeping and managing state of production items
export class ProductionContext {
    public groups = ref<IFulfillmentGroup[]>([]);
    public fulfillments = ref<IOperatorFulfillment[]>([]);
    public extendedFulfillments = ref<IExtendedOperatorFulfillment[]>([]);

    private operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
    private catalogDao = Factory.getInstance<CatalogDao>(CatalogDao);

    private groupsLoaded = false;
    private fulfillmentsLoaded = false;

    private isRefreshingFulfillments = ref<boolean>(false);
    private wasRefreshRequested = ref<boolean>(false);

    constructor() {
        dataEvents.operatorSocket.OrderOutstandingChanged.add(this.onOrderOutstandingChangedEvent, this);
        dataEvents.operatorSocket.PayLaterOrderConfirmed.add(this.onPayLaterOrderConfirmedEvent, this);
        dataEvents.operatorSocket.FulfillmentChanged.add(this.refreshFulfillments, this);
        dataEvents.operatorSocket.LinkedFulfillmentsChanged.add(this.refreshFulfillments, this);
        dataEvents.operatorSocket.FulfillmentPositionsChanged.add(this.refreshFulfillments, this);
        dataEvents.operatorSocket.LinkedFulfillmentsPositionsChanged.add(this.refreshFulfillments, this);

        dataEvents.operatorSocket.OrderCancelled.add(async (ev) => {
            // if no fulfillments for given order id found - no need to update
            if (!this.fulfillmentsForOrderExist(ev.orderId)) {
                return;
            }

            await this.refreshFulfillments();
        });

        OperatorClient.context.socket.onConnectionChanged.add(async (ev) => {
            if (ev == HubConnectionState.Connected && this.fulfillmentsLoaded) {
                await this.refreshFulfillments();
            }
        });

        this.setupWatchers();
    }

    public reset() {
        this.groupsLoaded = false;
        this.fulfillmentsLoaded = false;
        this.groups.value = [];
        this.fulfillments.value = [];
    }

    public async loadGroupsIfNeeded() {
        if (this.isLocationEmpty() || this.groupsLoaded) {
            return;
        }

        this.groupsLoaded = true;
        const groups = await this.catalogDao.getFulfillmentGroups();

        const filteredGroups = OperatorClient.context.settings?.fulfillmentGroups;
        if (filteredGroups !== undefined && filteredGroups.length > 0) {
            this.groups.value = groups.items.filter((c) => filteredGroups.find((d) => d.id === c.id) !== undefined);
        } else {
            this.groups.value = groups.items;
        }
    }

    public async loadFulfillmentIfNeeded() {
        if (this.isLocationEmpty() || this.fulfillmentsLoaded) {
            return;
        }

        this.fulfillmentsLoaded = true;
        try {
            this.isRefreshingFulfillments.value = true;
            const fulfillments = await this.operatorDao.getFulfillments();
            this.updateQueueLengths(fulfillments);
            this.fulfillments.value = fulfillments;
            operatorEvents.productionContext.FulfillmentsLoaded.dispatch();
        } catch (ex) {
            logError(ex);
            Popup.show({
                title: trans.general.error,
                message: trans.production.failedLoadingFulfillments,
                uniqueId: "failed-loading-fulfillments-popup",
            });
            throw ex;
        } finally {
            this.isRefreshingFulfillments.value = false;

            if (this.wasRefreshRequested.value) {
                this.wasRefreshRequested.value = false;
                this.refreshFulfillments();
            }
        }
    }

    public async reloadAll() {
        this.reset();
        await this.loadGroupsIfNeeded();
        await this.loadFulfillmentIfNeeded();
    }

    public async onPayLaterOrderConfirmedEvent(ev: IPayLaterOrderConfirmedEvent) {
        if (ev.fulfillmentHandling === FulfillmentHandling.None) {
            return;
        }

        /**
         * No need to refresh the list since the other event OnFulfillmentPositionsChanged will be sent to the frontend
         */
        if (ev.fulfillmentPositionDisplayMode === FulfillmentPositionDisplayMode.Position) {
            return;
        }

        if (ev.fulfillmentGroupIds.find((fg) => SocketEventHelper.triggerFulfillmentDataEvent(fg)) != null) {
            await this.refreshFulfillments();
        }
    }

    public async onOrderOutstandingChangedEvent(ev: IOrderOutstandingChangedEvent) {
        if (ev.fulfillmentHandling === FulfillmentHandling.None) {
            return;
        }

        if (ev.orderState === OrderState.Open) {
            return;
        }

        if (ev.fulfillmentGroupIds.some((fg) => SocketEventHelper.triggerFulfillmentDataEvent(fg))) {
            await this.refreshFulfillments();
        }
    }

    // loads all fulfillments and updates their state in this context
    public async refreshFulfillments() {
        if (this.isRefreshingFulfillments.value) {
            this.wasRefreshRequested.value = true;
            return;
        }

        try {
            this.isRefreshingFulfillments.value = true;
            const currentFulfillments = await this.operatorDao.getFulfillments();
            // update current fulfillments state with loaded data
            this.updateFulfillmentsData(currentFulfillments);
            this.updateProductionState();
        } catch (ex) {
            logError(ex);
            Popup.show({
                title: trans.general.error,
                message: trans.production.failedLoadingFulfillments,
                uniqueId: "failed-loading-fulfillments-popup",
            });
        } finally {
            this.isRefreshingFulfillments.value = false;

            if (this.wasRefreshRequested.value) {
                this.wasRefreshRequested.value = false;
                this.refreshFulfillments();
            }
        }
    }

    // todo: move it ideally to API
    // this logic is responsible mainly for automatic setting of fulfillment to ready state
    public updateProductionState() {
        const readyFulfillments = [] as IOperatorFulfillment[];
        for (const fulfillment of this.extendedFulfillments.value) {
            const newProductionState = FulfillmentHelper.getFulfillmentProductionState(fulfillment);

            if (fulfillment.productionState != newProductionState) {
                fulfillment.productionState = newProductionState;

                if (newProductionState == FulfillmentProductionState.Ready) {
                    readyFulfillments.push(fulfillment);
                }
            }
        }

        for (const fulfillment of readyFulfillments) {
            setTimeout(async () => {
                try {
                    Logging.LogApiCall("Sending request: 'SetFulfillmentReady'");
                    await this.operatorDao.setFulfillmentReady(fulfillment.id, {
                        orderId: fulfillment.orderId,
                    });
                } catch (ex) {
                    logError(ex);
                } finally {
                    Logging.LogApiCall("Request: 'SetFulfillmentReady' sent");
                }
            }, 4);
        }
    }

    /* state changes functions */
    // updates current fulfillments data by using newly loaded fulfillments
    private updateFulfillmentsData(updatedFulfillments: IOperatorFulfillment[]) {
        // update queue lengths
        this.updateQueueLengths(updatedFulfillments);

        // walk through all loaded fulfillments
        for (const newFulfillment of updatedFulfillments) {
            // if is found in the old list - update fulfillment
            if (this.fulfillmentExists(newFulfillment)) {
                this.updateFulfillment(newFulfillment);
            } else {
                this.addFulfillment(newFulfillment);
            }
        }

        // walk through all old fulfillments to find those to remove
        const fulfillmentsToRemove = [] as IOperatorFulfillment[];
        for (const oldFulfillment of this.fulfillments.value) {
            // if old fulfillment is not found in the updated list - it must have been removed
            if (!updatedFulfillments.some((f) => f.id == oldFulfillment.id)) {
                fulfillmentsToRemove.push(oldFulfillment);
            }
        }

        // remove all fulfillments marked to remove
        for (const fulfillmentToRemove of fulfillmentsToRemove) {
            this.removeFulfillment(fulfillmentToRemove);
        }
    }

    private addFulfillment(fulfillment: IOperatorFulfillment) {
        // add fulfillment to list
        this.fulfillments.value.push(fulfillment);

        // dispatch changed events
        operatorEvents.productionContext.FulfillmentAdded.dispatch(fulfillment);
    }

    private updateFulfillment(updatedFulfillment: IOperatorFulfillment) {
        // update fulfillment in the fulfillments list
        const idx = this.findFulfillmentIndex(updatedFulfillment);
        if (idx === -1) return;

        // continue update only if it's really updated
        const oldObjectState = JSON.stringify(this.fulfillments.value[idx]);
        const newObjectState = JSON.stringify(updatedFulfillment);
        if (oldObjectState === newObjectState) return;

        const previousState = this.fulfillments.value[idx].state;
        this.fulfillments.value[idx] = updatedFulfillment;

        // dispatch changed events
        operatorEvents.productionContext.FulfillmentChanged.dispatch({ ...updatedFulfillment, previousState });
    }

    private removeFulfillment(fulfillment: IOperatorFulfillment) {
        // find fulfillment in current list and remove it
        const idx = this.findFulfillmentIndex(fulfillment);
        if (idx === -1) return;

        this.fulfillments.value.splice(idx, 1);

        // dispatch changed events
        operatorEvents.productionContext.FulfillmentRemoved.dispatch(fulfillment);
    }

    /* extended fulfillments */
    private getExtendedFulfillment(fulfillment: IOperatorFulfillment) {
        return {
            ...fulfillment,
            fulfillmentGroup: this.getFulfillmentGroup(fulfillment),
            productionState: FulfillmentHelper.getFulfillmentProductionState(fulfillment),
        };
    }

    private getFulfillmentGroup(fulfillment: IOperatorFulfillment): IFulfillmentGroup | undefined {
        return fulfillment.fulfillmentGroupId !== undefined
            ? this.groups.value.find((c) => c.id === fulfillment.fulfillmentGroupId)
            : undefined;
    }

    /* helper functions */
    private fulfillmentExists(fulfillment: IOperatorFulfillment) {
        return this.fulfillments.value.some((f) => f.id == fulfillment.id);
    }

    private findFulfillmentIndex(fulfillment: IOperatorFulfillment) {
        return this.fulfillments.value.findIndex((c) => c.id === fulfillment.id);
    }

    private fulfillmentsForOrderExist(orderId: string) {
        return this.fulfillments.value.some((c) => c.orderId === orderId);
    }

    private isLocationEmpty() {
        return OperatorClient.context.settings?.location === undefined;
    }

    // #region OBSOLETE FUNCTIONS
    private fulfillmentGroupQueueLengths = ref<object>({});

    private updateQueueLengths(fulfillments: IOperatorFulfillment[]) {
        this.fulfillmentGroupQueueLengths.value = this.getFulfillmentGroupQueueLengths(fulfillments);
    }

    // todo: obsolete, it was used to determine max number of position increase when in Position mode
    public getFulfillmentGroupQueueLength(fulfillmentGroupId?: string) {
        const collectionFulfillmentGroupId = fulfillmentGroupId ?? "NONE";
        if (this.fulfillmentGroupQueueLengths && this.fulfillmentGroupQueueLengths[collectionFulfillmentGroupId]) {
            const currentQueueLength = this.fulfillmentGroupQueueLengths[collectionFulfillmentGroupId];
            return currentQueueLength;
        }
        return 0;
    }

    // todo: obsolete, it was used to determine max number of position increase when in Position mode
    private getFulfillmentGroupQueueLengths(fulfillments: IOperatorFulfillment[]) {
        const newFulfillmentGroupQueueLengths = {};
        for (const fulfillment of fulfillments) {
            const isQueuedFulfillment =
                fulfillment.state == FulfillmentState.InPreparation &&
                fulfillment.fulfillmentPositionDisplayMode == FulfillmentPositionDisplayMode.Position;

            if (!isQueuedFulfillment) continue;

            const fulfillmentGroupId = fulfillment.fulfillmentGroupId ?? "NONE";
            if (newFulfillmentGroupQueueLengths[fulfillmentGroupId] === undefined) {
                newFulfillmentGroupQueueLengths[fulfillmentGroupId] = 0;
            }

            newFulfillmentGroupQueueLengths[fulfillmentGroupId]++;
        }

        return newFulfillmentGroupQueueLengths;
    }
    // #endregion

    private setupWatchers() {
        // watcher to update extended fulfillments when fulfillments change
        watch(
            this.fulfillments,
            (fulfillments) => {
                this.extendedFulfillments.value = fulfillments.map((f) => this.getExtendedFulfillment(f));
            },
            { deep: true }
        );
    }

    /* Singleton instance */
    private static _instance = null as ProductionContext | null;

    public static getInstance(): ProductionContext {
        if (this._instance == null) {
            this._instance = new ProductionContext();
        }

        return this._instance;
    }
}
